<template>
	<Accessible
		name="taskdetails"
		class="task-drawer-container"
		:class="{
			'announcements-banner-tasks-open':
				store.showAnnouncementsBanner || store.askForNotificationPermission,
			'announcements-banner-tasks-close':
				!store.showAnnouncementsBanner && !store.askForNotificationPermission,
		}"
		:tabOrder="tabOrder"
		:wait="500"
		trapped
		autoFocus
		focusFirstItem
		ref="accessible"
	>
		<div class="task-drawer-wrapper">
			<div
				class="task-drawer"
				v-bind:class="{ 'task-drawer-wide': isWideDetail }"
			>
				<div v-if="isLoading" class="task-loader">
					<span>
						<img src="/images/pronto/3-dot-loader.gif" alt="Loading" />
						<p>
							{{ loaderText }}
						</p>
					</span>
				</div>

				<FailBanner v-if="showFail" @close="showFail = false">
					<template #alerttext>{{ alertText }}</template>
				</FailBanner>

				<div class="task-drawer-header">
					<input
						class="icon-button pointer"
						type="image"
						src="/images/common/large-grey-close.png"
						@click="close"
						:aria-label="$t('common_button_close')"
						role="button"
					/>
				</div>

				<div class="task-detail">
					<div class="task-title p-l-15">
						<Checkbox
							v-if="!isNewTask"
							v-model="isCompleted"
							pronto
							:loading="loading"
						/>
						<div class="task-title-new" @click.stop>
							<input
								:placeholder="$t('tasks_title_title')"
								v-model="title"
								@click.stop="$refs.titleTextarea.focus()"
								@input="inputTitle"
								:disabled="!canEditTitle"
								ref="titleTextarea"
							/>
						</div>
					</div>
					<div v-if="maxTitleLimit" class="over-text-limit">
						{{ $t('tasks_exceeds_maxchars') }}
					</div>

					<div class="sub-heading-no-divider">
						{{ $t('tasks_creator', [taskCreator]) }}
					</div>
					<div class="sub-heading-no-divider">
						{{ $t('tasks_details_heading') }}
					</div>

					<div class="vert-task-controls">
						<div
							@click="handleClick('calendar')"
							class="vert-task-control due-date-calendar"
							role="button"
							:aria-label="$t('tasks_due_date', [dueDateDisplay(dueDate)])"
						>
							<CalendarIcon type="regular" class="pronto-green" />
							<span
								class="vert-task-controls-span pronto-green pointer icon-button"
								>{{ $t('tasks_due_date', [dueDateDisplay(dueDate)]) }}</span
							>
						</div>

						<SimpleModal v-if="showCalendar" @close="showCalendar = false">
							<div class="due-date-picker">
								<DatePicker
									:disabled="disabled"
									v-model="dueDate"
									:min="minDate"
								/>
							</div>
						</SimpleModal>

						<div
							@click.stop="handleClick('assign')"
							class="vert-task-control assign-to"
							v-bind:class="{
								'task-tray-top-border': showAssignTo,
								disabled: !canChangeAssignee,
							}"
							role="button"
							:aria-label="
								!showAssignTo && !hasAssignee
									? $t('tasks_assignto_img_alt')
									: $t('tasks_assignedto', [assignedTitle])
							"
						>
							<UserIcon />
							<span
								class="vert-task-controls-span mar-l-17 icon-button"
								:class="{
									'pronto-green': showAssignTo || hasAssignee,
									pointer: canChangeAssignee,
								}"
								>{{ assignedTitle }}</span
							>
							<input
								v-if="showAssignTo"
								class="vert-task-control-close icon-button"
								type="image"
								src="/images/common/large-grey-close.png"
								@click.stop="showAssignTo = false"
								:aria-label="$t('common_button_close')"
								role="button"
							/>
						</div>
						<div
							v-if="showAssignTo"
							class="task-tray-assign"
							v-on-click-outside="outsideClose"
						>
							<div
								id="taskPerson"
								class="task-assign-tab pointer link-button"
								v-bind:class="{ 'task-assign-tab-active': personActive }"
								@click="toggleList"
							>
								{{
									$t(
										'contacts_userbubblepicker_option_people_single'
									).toUpperCase()
								}}
							</div>

							<div
								id="taskGroup"
								class="task-assign-tab pointer link-button"
								v-bind:class="{ 'task-assign-tab-active': groupActive }"
								@click="toggleList"
							>
								{{
									$t(
										'contacts_userbubblepicker_option_bubbles_single'
									).toUpperCase()
								}}
							</div>

							<div class="task-assign-list-wrapper">
								<UserGroupSearch
									v-if="groupActive"
									:config="{ maxheight: '430px' }"
									@selectedGroup="setAssigneeGroup"
								/>
								<UserSearch
									v-if="personActive"
									:config="{
										filtering: true,
										multiple: false,
										title: '',
										inModal: false,
										maxheight: '400px',
										loaderpadding: '20%',
										subMenuHeight: '200px',
										includeself: true,
									}"
									@selectedUsers="setAssigneeUser"
								/>
							</div>
						</div>

						<div
							@click="handleClick('reminder')"
							class="vert-task-control reminder"
							role="button"
							:aria-label="$t('tasks_reminder_img_alt')"
						>
							<BellIcon />
							<span
								v-bind:class="{ 'pronto-green': showReminder || hasReminder }"
								class="vert-task-controls-span mar-l-15 pointer icon-button"
								>{{ reminderDateDisplay(reminderDate) }}</span
							>
						</div>

						<!-- REMINDER DATE PICKER -->
						<DatePickerModal
							v-if="showReminder"
							v-model="reminderDate"
							showTime
							noPastDates
							:confirmText="$t('search_date_filter_setdate')"
							:cancelText="
								reminderDate ? $t('groupsettings_people_remove_button') : null
							"
							@cancel="reminderDate = null"
							@close="onReminderPickerClose"
						/>

						<div
							class="vert-task-control attachment"
							ref="attachmentContainer"
							:aria-label="$t('tasks_attachment_title')"
						>
							<label for="file-input" class="pointer"></label>
							<label for="file-input" role="button">
								<PaperclipIcon />
								<span
									v-show="!hasMedia"
									class="vert-task-controls-span mar-l-12 icon-button pointer"
									>{{ $t('tasks_attachment_title') }}</span
								>
							</label>

							<input
								v-if="!hasMedia"
								class="hidden"
								id="file-input"
								type="file"
								ref="fileInput"
								@change="onFileChange"
							/>

							<MediaPreview
								v-if="mediaItems.length"
								:mediaItems="mediaItems"
								@remove="onRemoveMedia"
								@click="onClickMediaItem"
							/>
						</div>
					</div>

					<div class="task-divider"></div>

					<div
						class="sub-heading-no-divider task-notes-header task-notes-header-border"
					>
						<span>{{ $t('tasks_notes_title') }}</span>
					</div>
					<div class="pre-wrap task-notes task-notes-border" ref="notesBorder">
						<textarea
							rows="1"
							class="pointer notes-textarea"
							@click="hasFocus = true"
							@input="inputNotes"
							:placeholder="$t('tasks_notes_title')"
							v-model="notes"
							ref="notesTextarea"
						></textarea>
					</div>
					<div v-if="maxNotesLimit" class="over-text-limit">
						{{ $t('tasks_exceeds_maxchars') }}
					</div>

					<div class="task-buttons">
						<Button large @click="close" v-if="showSave">
							{{ $t('common_button_cancel') }}
						</Button>

						<Button
							v-if="showSave"
							:disabled="saveDisabled"
							@click="handleSave"
							large
							pronto
						>
							{{ $t('common_button_save') }}
						</Button>

						<Button
							v-if="canDiscuss"
							@click="prepDiscuss"
							large
							:icon="CommentsIcon"
							:iconProps="{ type: 'regular' }"
						>
							{{ $t('tasks_action_discuss') }}
						</Button>

						<Button
							v-if="!isClaimed && !isEditing"
							@click="claimTask"
							large
							:icon="FlagSwallowtailIcon"
							:iconProps="{ type: 'regular' }"
						>
							{{ $t('tasks_action_claim') }}
						</Button>

						<Button
							v-if="isOwner && taskdata.id"
							@click="confirmDelete"
							large
							danger
							:icon="TrashCanIcon"
							:iconProps="{ type: 'regular' }"
						>
							{{ $t('tasks_action_delete') }}
						</Button>
					</div>
				</div>
			</div>
		</div>

		<DiscussTask
			v-if="discussTask"
			@closeDiscuss="discussTask = false"
			v-bind:taskdata="discussData"
			@closeAll="closeAll"
		/>
	</Accessible>
</template>

<script>
import { markRaw } from 'vue'
import store, { getChat, getGroupChats, sortChatsByDisplayName } from '../store'
import moment from 'moment'
import { post } from '../helpers/api'
import {
	SimpleModal,
	DatePicker,
	Checkbox,
	Button,
	showAlertModal,
} from './common'
import DiscussTask from './DiscussTask'
import FailBanner from './FailBanner'
import UserGroupSearch from './UserGroupSearch'
import UserSearch from './UserSearch'
import { isFirefox } from '@/store'
import MediaPreview from '@/components/MessageInput/MediaPreview'
import { addEscapeListener, removeEscapeListener } from '@/helpers/escape'
import { normalizeMediaItem, dayOfWeek } from '@/helpers/utils'
import {
	TrashCanIcon,
	FlagSwallowtailIcon,
	CommentsIcon,
	PaperclipIcon,
	CalendarIcon,
	BellIcon,
	UserIcon,
} from '@/icons'
import { prepFilesForUpload } from '@/api/message'
import Accessible from '@/components/common/Accessible'
import { DatePickerModal } from '@/components/common'

export default {
	components: {
		DatePicker,
		SimpleModal,
		DiscussTask,
		FailBanner,
		UserGroupSearch,
		UserSearch,
		MediaPreview,
		Checkbox,
		PaperclipIcon,
		Button,
		CalendarIcon,
		BellIcon,
		UserIcon,
		Accessible,
		DatePickerModal,
	},
	props: ['taskdata', 'loading'],
	data() {
		return {
			store,
			isFirefox,
			isOwner: false,
			disabled: {
				to: '',
			},
			showCalendar: false,
			showReminder: false,
			showAssignTo: false,
			showNotes: false,
			personActive: true,
			groupActive: false,
			hasFocus: false,
			discussTask: false,
			isNewTask: false,
			isCompleted: null,
			showFail: false,
			alertText: '',
			isEditing: false,
			isLoading: false,
			hasAttachment: false,
			isAttaching: false,
			attachmentError: false,
			maxTitleLimit: false,
			maxNotesLimit: false,
			isClaimed: true,
			disableSave: false,
			errorText: '',
			filedata: {},
			media: null,
			loaderText: '',
			userList: [],
			groupList: [],
			personMatches: [],
			groupMatches: [],
			search: '',
			searching: false,
			title: '',
			dueDate: '',
			reminderDate: '',
			notes: '',
			isAssigned: false,
			assignedUser: '',
			assignedGroup: '',
			assignedTitle: this.$t('tasks_assignee_title'),
			discussData: {},
			delay: 250,
			throttled: false,
			maxTitleChars: 1000,
			maxNotesChars: 5000,
			editMode: false,
			FlagSwallowtailIcon: markRaw(FlagSwallowtailIcon),
			TrashCanIcon: markRaw(TrashCanIcon),
			CommentsIcon: markRaw(CommentsIcon),
		}
	},

	emits: [
		'close',
		'taskdeleted',
		'taskcomplete',
		'taskuncomplete',
		'closeUnclaimed',
	],

	watch: {
		editMode: function (editMode) {
			if (editMode) {
				this.autoSizeNotes()
			}
		},

		search: function (val, oldVal) {
			if (this.search.length > 0) {
				this.searching = true
				if (this.personActive) {
					this.personMatches = this.userList.filter(function (match) {
						return match.name.toLowerCase().indexOf(val.toLowerCase()) > -1
					})
				} else {
					this.groupMatches = this.groupList.filter(function (match) {
						return match.title.toLowerCase().indexOf(val.toLowerCase()) > -1
					})
				}
			} else {
				this.searching = false
			}
		},

		title: function (val, oldVal) {
			if (oldVal) {
				this.isEditing = true
			} else {
				if (val != this.taskdata.title) {
					this.isEditing = true
				}
			}
		},

		dueDate: function (val, oldVal) {
			if (oldVal && val === null) {
				// if user selects already selected date, value becomes null
				// so this resets it back to the previously selected value
				this.dueDate = oldVal
				return
			}
			if (oldVal) {
				this.reminderDate = ''
				this.isEditing = true
				this.showCalendar = false
			} else {
				if (val != this.taskdata.due) {
					this.isEditing = true
					this.showCalendar = false
				}
			}
		},

		assignedGroup: function (val, oldVal) {
			if (oldVal) {
				this.isEditing = true
			} else {
				if (val != this.taskdata.bubble_id) {
					this.isEditing = true
				}
			}
		},

		assignedUser: function (val, oldVal) {
			if (oldVal) {
				this.isEditing = true
			} else {
				if (val != this.taskdata.assigneeuser_id) {
					this.isEditing = true
				}
			}
		},

		reminderDate: function (val, oldVal) {
			if (oldVal) {
				this.isEditing = true
			} else {
				if (!moment(val).isSame(this.taskdata.reminder_local, 'day')) {
					this.isEditing = true
				}
			}
		},

		notes: function (val, oldVal) {
			if (oldVal) {
				this.isEditing = true
			} else {
				if (val != this.taskdata.notes) {
					this.isEditing = true
				}
			}
		},

		isCompleted(isCompleted, prev) {
			if (prev === null) return
			this.$nextTick(() => {
				if (this.isCompleted) {
					this.completeTask()
				} else {
					this.unCompleteTask()
				}
			})
		},

		serverUploaded: function (val, oldVal) {
			if (this.store.uploadCompleted) {
				this.uploadCompleted()
			}
		},
	},

	mounted() {
		let today = new Date()
		if (this.taskdata.id) {
			this.title = this.taskdata.title
			this.dueDate = this.taskdata.due
			if (this.taskdata.taskmedia[0]) {
				this.media = normalizeMediaItem(this.taskdata.taskmedia[0])
			}

			if (this.taskdata.reminder_local) {
				this.reminderDate = this.taskdata.reminder_local
			}

			this.notes = this.taskdata.notes
			this.assignedTitle = this.taskdata.assignedTitle

			this.isCompleted = !!this.taskdata.completed

			if (this.taskdata.assigneeuser_id) {
				this.assignedUser = this.taskdata.assigneeuser_id
			} else {
				this.assignedGroup = this.taskdata.bubble_id
			}

			this.isAssigned = true

			if (this.taskdata.user_id === this.store.auth.user.id) {
				this.isOwner = true
			}

			if (!this.taskdata.assigneeuser_id && !this.isNewTask) {
				this.isClaimed = false
			} else {
				this.isClaimed = true
			}
		} else {
			this.isOwner = true
			this.isNewTask = true
			this.dueDate = today
		}

		let yesterday = new Date(today)
		yesterday.setDate(today.getDate() - 1)

		this.disabled.to = yesterday

		this.minutes = Array.from(Array(60).keys())
			.map((x) => (x < 10 ? `0${x}` : x + ''))
			.map((x) => ({ text: x, value: x }))

		this.hours = Array.from(Array(12).keys())
			.map((x) => x + 1)
			.map((x) => (x < 10 ? `0${x}` : x + ''))
			.map((x) => ({ text: x, value: x }))

		this.times = [
			{
				text: 'AM',
				value: 'AM',
			},

			{
				text: 'PM',
				value: 'PM',
			},
		]

		// this.getOrgUsers();
		this.getGroups()

		this.$nextTick(function () {
			this.autoSizeTitle()
			if (!this.accessibilityMode) {
				this.$refs.titleTextarea.focus()
			}
		})

		window.addEventListener('resize', this.onWindowResize)

		this.escapeId = addEscapeListener(() => this.close())

		this.$onEventBus('upload-change', (media) => {
			if (this.media?.tempId !== media.tempId) return
			if (!media.isComplete && !media.isFailed) return
			if (media.isFailed && media.uploadProgress === 100) {
				this.media = normalizeMediaItem({
					...this.media,
					mediatype: mediaTypes.FILE,
					isComplete: true,
					isFailed: false,
				})
			} else if (media.isFailed) {
				this.media = null
				this.$alert.error()
			} else {
				this.media = {
					...this.media,
					isComplete: media.isComplete,
					isFailed: media.isFailed,
				}
			}
		})
	},

	beforeUnmount() {
		window.removeEventListener('resize', this.onWindowResize)
		removeEscapeListener(this.escapeId)
	},

	computed: {
		hasNotes() {
			if (this.notes) {
				return true
			} else {
				return false
			}
		},

		hasAssignee() {
			if (this.isAssigned) {
				return true
			} else {
				return false
			}
		},

		hasReminder() {
			if (this.reminderDate) {
				return true
			} else {
				return false
			}
		},

		hasMedia() {
			return !!this.media
		},

		hasChanged() {
			const {
				id,
				title,
				due,
				notes,
				reminder_local,
				assigneeuser_id,
				bubble_id,
				taskmedia,
			} = this.taskdata
			if (!id) return false
			const fields = [
				[this.title, title],
				[this.dueDate, due],
				[this.reminder_local, reminder_local],
				[this.notes, notes],
				[!!this.media, !!taskmedia?.length],
			]
			if (fields.some(([a, b]) => a !== b)) return true
			if (this.assignedUser) return this.assignedUser !== assigneeuser_id
			if (this.assignedGroup) return this.assignedGroup !== bubble_id
			return false
		},

		showSave() {
			return !this.taskdata.id || this.hasChanged
		},

		saveDisabled() {
			if (this.media?.tempId && !this.media.isComplete) return true
			if (this.taskdata.id) return !this.hasChanged
			return !this.hasRequired
		},

		hasRequired() {
			return (
				!!this.title &&
				!!this.dueDate &&
				!!this.isAssigned &&
				this.title.length <= 1000
			)
		},

		canDiscuss() {
			if (this.isNewTask) return false
			if (this.isCreator && this.isAssignee) return false
			return true
		},

		isCreator() {
			return this.taskdata.user?.id === this.store.auth.user.id
		},

		isAssignee() {
			return this.taskdata.assigneeuser_id === this.store.auth.user.id
		},

		canChangeAssignee() {
			return this.isCreator
		},

		canEditTitle() {
			return this.isCreator || this.isAssignee
		},

		taskCreator() {
			if (this.taskdata.user.id) {
				return this.taskdata.user.firstname + ' ' + this.taskdata.user.lastname
			} else {
				let creator = this.store.getUser(this.taskdata.user_id)
				return creator.firstname + ' ' + creator.lastname
			}
		},

		isWideDetail() {
			if (this.taskdata.parent !== 'Tasks.vue') {
				return true
			} else {
				return false
			}
		},

		serverUploaded() {
			return this.store.uploadCompleted
		},

		minDate() {
			const { id, created_at } = this.taskdata || {}
			return id ? moment.utc(created_at).local().toDate() : new Date()
		},

		mediaItems() {
			if (this.media) return [this.media]
			return []
		},

		reminder_local() {
			// when sending the reminder date to the API, we need to send
			// the reminder_local property in the local time, but without
			// a timezone. This should be the exact date/time the user selected
			// in the date picker, NOT the converted UTC equivalent. To
			// accomplish that, this removes the utc offset from the date.
			if (this.reminderDate) {
				return moment(this.reminderDate)
					.utcOffset(0, true)
					.format('YYYY-MM-DD HH:mm:ss')
			} else {
				return null
			}
		},

		tabOrder() {
			const tabs = [
				{ css: '.task-drawer-header .icon-button', clickOnEnter: true },
				{ css: '.task-title .pronto-checkbox', clickOnEnter: true },
				{ css: '.task-title .task-title-new > input', clickOnEnter: true },
				{ css: '.due-date-calendar', clickOnEnter: true },
				{ css: '.vert-task-control.assign-to', clickOnEnter: true },
				{ css: '.vert-task-control.reminder', clickOnEnter: true },
			]
			if (this.hasMedia) {
				tabs.push({
					css: '.media-preview-items .media-item',
					clickOnEnter: true,
				})
				tabs.push({
					css: '.media-preview-items .media-preview-remove',
					clickOnEnter: true,
				})
			} else {
				tabs.push({
					css: '.vert-task-control.attachment input',
					clickOnEnter: true,
				})
			}

			tabs.push({ css: '.notes-textarea', clickOnEnter: true })
			if (this.showSave) {
				tabs.push({
					all: '.task-buttons .pronto-btn',
					clickOnEnter: true,
					save: true, // when showSave becomes enabled, this forces tabOrder to reset
				})
			} else {
				tabs.push({
					all: '.task-buttons .pronto-btn',
					clickOnEnter: true,
				})
			}
			return tabs
		},
	},

	methods: {
		editNotes() {
			this.editMode = true
			this.hasFocus = true
			this.$nextTick(() => {
				this.autoSizeNotes()
			})
		},

		onWindowResize() {
			if (!this.throttled) {
				this.autoSizeTitle()
				this.autoSizeNotes()
				this.throttled = true
				setTimeout(function () {
					this.throttled = false
				}, this.delay)
			}
		},

		handleClick(option) {
			if (option == 'calendar') {
				this.showReminder = false
				this.showCalendar = !this.showCalendar
				this.showAssignTo = false
				this.showNotes = false
			} else if (option == 'assign') {
				if (!this.canChangeAssignee) return
				// this.getOrgUsers();
				this.getGroups()
				this.showReminder = false
				this.showCalendar = false
				this.showAssignTo = !this.showAssignTo
				this.showNotes = false
			} else if (option == 'reminder') {
				this.showReminder = !this.showReminder
				this.showCalendar = false
				this.showAssignTo = false
				this.showNotes = false
				if (this.showReminder) {
					this.calcReminder()
				}
			} else if (option == 'notes') {
				this.showReminder = false
				this.showCalendar = false
				this.showAssignTo = false
				this.showNotes = !this.showNotes
			}
		},

		handleClickAndFocus(option) {
			this.handleClick(option)
			this.$nextTick(() => {
				this.$refs.searchAssignTo.focus()
			})
		},

		handleSave() {
			if (this.isNewTask) {
				this.createTask()
			} else {
				this.updateTask(true)
			}
		},

		close() {
			this.store.taskShown = ''
			this.$emit('close')
		},

		dueDateDisplay(date) {
			// This date is a "floating date" which means it should be
			// interpretted as being in the user's local timezone
			const DOW = this.dayOfWeek(date)

			return ['TODAY', 'TOMORROW', 'YESTERDAY'].includes(DOW)
				? this.$t(`common_${DOW.toLowerCase()}`)
				: moment(date).format('LL')
		},

		reminderDateDisplay(date) {
			if (!date) return this.$t('tasks_reminder_title')
			return moment(date).format('LL, LT')
		},

		dayOfWeek(date) {
			// This date is a "floating date" which means it should be
			// interpretted as being in the user's local timezone
			return dayOfWeek(date)
		},

		toggleList() {
			this.personActive = !this.personActive
			this.groupActive = !this.groupActive
		},

		getOrgUsers() {
			let org_id = store.auth.user.organizations[0].id
			post('/api/v1/organization.users', { organization_id: org_id })
				.then(({ data }) => {
					var user_array = []
					if (data.users.length > 0) {
						data.users.forEach(function (user, index) {
							var userObj = {
								id: user.id,
								name: user.firstname + ' ' + user.lastname,
							}

							user_array.push(userObj)
						})

						this.userList = this.store.sortedUserList(user_array)
					}
				})
				.catch((error) => {
					console.error(error)
				})
		},

		getGroups() {
			this.groupList = getGroupChats().sort(sortChatsByDisplayName)
		},

		outsideClose() {
			if (this.showCalendar == true) {
				this.showCalendar = false
			} else if (this.showAssignTo == true) {
				this.showAssignTo = false
			} else if ((this.showNotes = true)) {
				this.showNotes = false
			}
		},

		createTask() {
			this.loaderText = this.$t('tasks_create_hud')
			this.isLoading = true

			let post_data = {
				organization_id: store.auth.user.organizations[0].id,
				uuid: crypto.randomUUID(),
				title: this.title,
				notes: this.notes,
				due: moment(this.dueDate).format('YYYY-MM-DD'),
			}

			if (this.reminder_local) {
				post_data.reminder_local = this.reminder_local
			}

			if (this.assignedUser) {
				post_data.assigneeuser_id = this.assignedUser
			} else {
				post_data.bubble_id = this.assignedGroup
			}

			if (this.media) {
				post_data.taskmedia = [this.media]
			}

			post('/api/v1/task.create', post_data)
				.then(({ data }) => {
					this.isLoading = false
					this.store.tasks.open.push(data.task)
					this.store.updateTaskStats()
					this.close()
				})
				.catch((error) => {
					console.error(error)
				})
		},

		setAssigneeUser(users) {
			var data = users[0]

			this.assignedUser = data.id
			this.assignedTitle = data.firstname + ' ' + data.lastname
			this.assignedGroup = ''

			this.isAssigned = true
			this.showAssignTo = false
		},

		setAssigneeGroup(data) {
			this.assignedGroup = data.id
			this.assignedTitle = data.displayname
			this.assignedUser = ''

			this.isAssigned = true
			this.showAssignTo = false
		},

		calcReminder() {
			if (!this.reminderDate) {
				let today = moment().local().format('YYYY-MM-DD HH:mm:ss')

				if (moment(this.dueDate).isBefore(today, 'd')) {
					this.reminderDate = today
				} else {
					this.reminderDate = this.dueDate
				}

				this.minute = '00'

				if (
					moment(today).isSame(this.dueDate, 'd') ||
					moment(this.dueDate).isBefore(today, 'd')
				) {
					var m = moment(today)
					var nextHour =
						m.minute() || m.second()
							? m.add(1, 'hour').startOf('hour')
							: m.startOf('hour')

					if (m.hour() < 9) {
						this.hour = '09'
						this.time = 'AM'
					} else if (moment(today).add(5, 'minutes').isBefore(nextHour)) {
						if (moment(nextHour).hour() > 12) {
							//I have to do these because the moment library drops the leading zero when you format the time with a 12 hour format and use the hour() function on it.  LAME.
							let current_hour = moment(nextHour).hour() - 12
							if (current_hour.toString().length == 1) {
								current_hour = '0' + current_hour
							}

							this.hour = current_hour
						} else {
							this.hour = moment(nextHour).hour()
						}
						this.time = moment(nextHour).format('A')
					} else {
						//I have to do these because the moment library drops the leading zero when you format the time with a 12 hour format and use the hour() function on it.  LAME.
						if (moment(nextHour).add(1, 'hour').hour() > 12) {
							let upcoming_hour = moment(nextHour).add(1, 'hour').hour() - 12
							if (upcoming_hour.toString().length == 1) {
								upcoming_hour = '0' + upcoming_hour
							}

							this.hour = upcoming_hour
						} else {
							this.hour = moment(nextHour).add(1, 'hour').hour()
						}
						this.time = moment(nextHour).format('A')
					}
				} else {
					this.hour = '09'
					this.time = 'AM'
				}
			}
		},

		removeReminder() {
			this.reminderDate = ''
			this.hour = ''
			this.minute = ''
			this.time = ''
			this.showReminder = false
		},

		confirmDelete() {
			const close = showAlertModal({
				title: this.$t('tasks_action_delete'),
				description: this.$t('tasks_confirmdelete_message'),
				confirmText: this.$t('tasks_action_delete'),
				hideCancel: false,
				danger: true,
				onConfirm: () => {
					close()
					this.deleteTask()
				},
			})
		},

		deleteTask() {
			this.loaderText = this.$t('tasks_delete_hud')
			this.isLoading = true
			post('/api/v1/task.delete', { task_id: this.taskdata.id })
				.then(({ data }) => {
					this.isLoading = false
					this.$emit('taskdeleted', {
						task_id: this.taskdata.id,
						completed: this.isCompleted,
					})
					this.close()
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error(error)
				})
		},

		completeTask() {
			this.$emit('taskcomplete', this.taskdata)
		},

		unCompleteTask() {
			this.$emit('taskuncomplete', this.taskdata)
		},

		updateTask(showLoading) {
			if (showLoading) {
				this.loaderText = this.$t('tasks_update_hud')
				this.isLoading = true
			}

			let post_data = {
				task_id: this.taskdata.id,
				due: moment(this.dueDate).format('YYYY-MM-DD'),
				bubble_id: null,
				assigneeuser_id: null,
			}

			if (this.title) {
				post_data.title = this.title
			}

			if (this.reminder_local) {
				post_data.reminder_local = this.reminder_local
			}

			if (this.assignedUser) {
				post_data.assigneeuser_id = this.assignedUser
			} else {
				post_data.bubble_id = this.assignedGroup
			}

			if (this.notes) {
				post_data.notes = this.notes
			}

			post_data.taskmedia = this.media ? [this.media] : []

			post('/api/v1/task.update', post_data)
				.then(({ data }) => {
					this.isLoading = false
					this.isEditing = false
					this.media = null

					if (data.task.completed) {
						let taskObj = this.store.tasks.completed.findIndex(
							(obj) => obj.id == data.task.id
						)
						this.store.tasks.completed[taskObj] = data.task
					} else {
						let taskObj = this.store.tasks.open.findIndex(
							(obj) => obj.id == data.task.id
						)
						this.store.tasks.open[taskObj] = data.task
					}

					this.store.updateTaskStats()
					this.$emit('close')
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error(error)
				})
		},

		async onFileChange(e) {
			this.store.uploadCompleted = false
			this.store.uploadedMedia = {}
			this.isAttaching = true
			const file = e.target.files[0]

			if (!file || !file.type) return

			this.filedata.name = file.name
			this.filedata.type = file.type
			this.filedata.size = file.size

			const files = await prepFilesForUpload(e.target.files)
			this.media = files[0]
		},

		onRemoveMedia() {
			this.media = null
		},

		onClickMediaItem(item) {
			this.$mediaItems([item], item.tempId)
		},

		clickFileInput() {
			document.querySelector('#file-input').click()
		},

		prepDiscuss() {
			this.discussData.id = this.taskdata.id
			this.discussData.title = this.title
			this.discussData.assignedTitle = this.assignedTitle
			this.discussData.bubble_id = this.assignedGroup
			this.discussData.assigneeuser_id = this.assignedUser
			this.discussData.due = this.dueDate

			this.discussTask = true
		},

		autoSizeTitle(e) {
			if (this.$refs.titleTextarea.scrollHeight) {
				let rows = Math.ceil(this.$refs.titleTextarea.scrollHeight / 45)
				//check for backspace...
				// if (e === 8) {
				// 	let lines = this.$refs.titleTextarea.value.split('\n');
				// 	console.log(lines);
				// 	if (!lines[lines.length - 1]) {
				// 		this.$refs.titleTextarea.rows = rows - 1;
				// 	}
				// }

				if (this.title.length === 0) {
					if (this.$refs.titleTextarea) {
						this.$refs.titleTextarea.rows = 1
					}
					return
				}

				if (rows > 1) {
					this.$refs.titleTextarea.rows = rows
				}
			}
		},

		autoSizeNotes() {
			if (this.notes) {
				let rows = Math.ceil(this.$refs.notesTextarea.scrollHeight / 26)

				if (this.notes && this.notes.length === 0) {
					this.$refs.notesTextarea.rows = 1
					this.$refs.notesBorder.style.height = '54px'
					return
				}

				if (rows > 1) {
					this.$refs.notesTextarea.rows = rows
					let height = rows * 22 + 32
					this.$refs.notesBorder.style.height = height + 'px'
				} else {
					this.$refs.notesTextarea.rows = rows
					this.$refs.notesBorder.style.height = '54px'
				}
			}
		},

		inputTitle(e) {
			let chars = this.title.trim()
			if (
				this.maxTitleChars - new TextEncoder('utf-8').encode(chars).length <=
				-1
			) {
				this.maxTitleLimit = true
			} else {
				this.maxTitleLimit = false
			}

			this.autoSizeTitle(e)
		},

		inputNotes(e) {
			let chars = this.notes.trim()
			if (
				this.maxNotesChars - new TextEncoder('utf-8').encode(chars).length <=
				-1
			) {
				this.maxNotesLimit = true
			} else {
				this.maxNotesLimit = false
			}

			this.autoSizeNotes()
		},

		getAssignee(task) {
			if (task.bubble_id && !task.assigneeuser_id) {
				return getChat(task.bubble_id).displayname
			} else {
				return task.assigneeuser.firstname + ' ' + task.assigneeuser.lastname
			}
		},

		claimTask() {
			this.loaderText = this.$t('tasks_claim_hud')
			this.isLoading = true

			let post_data = {
				task_id: this.taskdata.id,
				organization_id: store.auth.user.organizations[0].id,
			}

			post('/api/v1/task.claim', post_data)
				.then(({ data }) => {
					this.isLoading = false
					// this.refreshState(data.task);
					let taskObj = this.store.tasks.open.findIndex(
						(obj) => obj.id == data.task.id
					)
					this.store.tasks.open[taskObj] = data.task
					this.isClaimed = true
					if (data.task.assigneeuser_id) {
						this.assignedUser = data.task.assigneeuser_id
					} else {
						this.assignedGroup = data.task.bubble_id
					}

					this.assignedTitle = this.getAssignee(data.task)

					this.store.updateTaskStats()
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error(error)
					this.isLoading = false
				})
		},

		closeAll() {
			this.$emit('closeUnclaimed')
		},

		onReminderPickerClose() {
			this.showReminder = false
			if (this.accessibilityMode) {
				this.$nextTick(() => {
					this.$refs.accessible?.focusMe?.()
				})
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.announcements-banner-tasks-open {
	top: 130px;
	transition-duration: 400ms;
	transition-delay: 1200ms;
	transition-timing-function: ease-in-out;
	transition-property: all;
}
.announcements-banner-tasks-close {
	top: 0;
}

.pronto-green {
	color: var(--teal-300) !important;
}
.delete-modal-wrapper {
	padding: 50px;
	text-align: center;
	span {
		font-family: Avenir-Book;
		font-size: 14px;
		color: #8a8d93;
		display: block;
	}
}

.vert-task-control {
	.pronto-icon {
		color: var(--neutral-500);
	}
}
.due-date-picker {
	padding: 36px;
}
.reminder-date-picker {
	padding: 36px;
	> button {
		display: block;
		margin: 24px auto 0 auto;
	}
}
.task-title {
	display: flex;
	width: 100%;
	input {
		font-size: 30px;
		min-height: 35px !important;
		color: #4f5053 !important;
		caret-color: var(--teal-300);
		border: none;
		width: 100%;
		outline: none;
	}
	.pronto-checkbox {
		font-size: 24px;
	}
}

.attachment {
	.pronto-icon {
		color: var(--neutral-400);
	}
	.media-preview-container {
		margin-top: -36px;
		margin-left: 32px;
	}
}

.task-buttons {
	@include flex(16px);
}

.delete-task-icon,
.claim-task-icon {
	font-size: 19px;
}
.discuss-task-icon {
	font-size: 22px;
}

.task-notes textarea {
	line-height: normal;
	caret-color: var(--teal-300);
}

.display-notes {
	border-color: transparent;
	border-style: none;
	color: #4b4d50 !important;
	font-family: Avenir-Roman;
	font-size: 16px;
	font-weight: 400;
	margin-bottom: 45px;
	outline: none;
	resize: none;
	text-align: left;
	width: 100%;
	letter-spacing: normal;
	word-spacing: normal;
	line-height: normal;
	text-transform: none;
	text-indent: 0px;
	text-shadow: none;
	overflow-wrap: break-word;
	padding: 2px;
	white-space: pre-wrap;
	cursor: pointer;
	&:empty:before {
		content: attr(data-placeholder);
		color: #8a8d93;
		display: block;
	}
}

.task-assign-list-wrapper {
	:deep(.user-search-list) {
		height: 270px !important;
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';
@include overrideScheme('dark') {
	.task-drawer {
		background-color: var(--neutral-300);
		border: solid 1px var(--neutral-300);
		.task-title-new {
			input,
			span {
				background-color: var(--neutral-300);
				color: var(--neutral-800) !important;
			}
		}
		.sub-heading-no-divider {
			color: var(--neutral-800);
		}
		.vert-task-controls-span {
			color: var(--neutral-600);
		}
		.task-notes-header-border,
		.task-divider,
		.task-tray-top-border {
			border-color: var(--neutral-200);
			opacity: 1;
		}
		.task-tray-top-border {
			background-color: var(--neutral-100);
		}
		.task-tray-assign {
			background-color: var(--neutral-100);
			border-color: var(--neutral-200);
			box-shadow: none;
			.user-search-filter,
			#userSearchBox,
			#groupSearchBox {
				background-color: var(--neutral-0);
			}
			#userSearchBox,
			#groupSearchBox {
				border: solid 1px var(--neutral-200);
				input {
					background-color: var(--neutral-0);
					color: var(--neutral-600);
				}
			}
			.user-search-filter {
				.filter-button,
				.main-menu,
				.filter-button-arrow,
				.sub-menu {
					background-color: var(--neutral-100);
					border-color: var(--neutral-200);
				}
				.main-item {
					div {
						color: var(--neutral-800);
					}
					span {
						color: var(--neutral-600);
					}
					&:hover {
						background-color: var(--neutral-0);
						.filter-button-arrow {
							background-color: var(--neutral-0) !important;
						}
					}
				}
				.filter-span-text {
					color: var(--neutral-800);
				}
				.sub-item {
					.category-name {
						background-color: var(--neutral-0);
						color: var(--neutral-800);
					}
					.group {
						border-color: var(--neutral-200);
						color: var(--neutral-600);
					}
				}
			}

			.scroll-fade-wrapper-top {
				background-image: linear-gradient(
					to top,
					rgba(16, 16, 16, 0) 1%,
					#101010 50%,
					#101010 100%
				);
			}
			.scroll-fade-wrapper-bottom {
				background-image: linear-gradient(
					to bottom,
					rgba(16, 16, 16, 0) 1%,
					#101010 50%,
					#101010 100%
				);
			}
		}

		#userSearch li,
		.group-search-list li {
			border-color: var(--neutral-200) !important;
		}
		.user-search-content-container {
			.missing-img {
				background-color: var(--neutral-200);
				color: val(--neutral-800);
			}
			.user-name {
				.ui-main {
					color: var(--neutral-600) !important;
				}
				.ui-main-bold {
					color: var(--neutral-800) !important;
				}
			}
		}
		.task-notes {
			border-color: var(--neutral-200);
			box-shadow: none;
			textarea,
			textarea::placeholder {
				background-color: var(--neutral-300);
				color: var(--neutral-800) !important;
			}
		}
		.attachment-wrapper {
			border: 1px solid var(--neutral-200);
		}
	}
	.ios-spinner {
		background: var(--neutral-300) !important;
	}

	.reminder-date-picker {
		background-color: var(--neutral-300);
		.pronto-link-button {
			background-color: var(--neutral-300);
		}
	}
}
</style>
